import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './config';
import { queueWelcomeEmail } from './emails';

// Email/Password Authentication
export const signUpWithEmail = async (email, password, displayName) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update user profile with display name
    await updateProfile(user, {
      displayName: displayName
    });

    // Create user document in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      displayName: displayName,
      createdAt: new Date().toISOString(),
      subscription: {
        plan: 'free',
        status: 'active'
      }
    });

    // Queue welcome email
    try {
      await queueWelcomeEmail(user.email, displayName);
    } catch (emailError) {
      console.error('Failed to queue welcome email:', emailError);
      // Don't fail signup if email queueing fails
    }

    return user;
  } catch (error) {
    console.error('Error signing up:', error);
    throw error;
  }
};

export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

// Magic Link Authentication
export const sendMagicLink = async (email) => {
  const actionCodeSettings = {
    // URL you want to redirect back to after email link is clicked
    url: `${window.location.origin}/complete-signin`,
    handleCodeInApp: true,
  };

  try {
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    // Save the email locally so we can complete sign in
    window.localStorage.setItem('emailForSignIn', email);
    return true;
  } catch (error) {
    console.error('Error sending magic link:', error);
    throw error;
  }
};

export const completeMagicLinkSignIn = async (email) => {
  if (isSignInWithEmailLink(auth, window.location.href)) {
    try {
      // If email is not provided, try to get it from localStorage
      const emailToUse = email || window.localStorage.getItem('emailForSignIn');

      if (!emailToUse) {
        throw new Error('Email is required to complete sign in');
      }

      const result = await signInWithEmailLink(auth, emailToUse, window.location.href);

      // Clear email from storage
      window.localStorage.removeItem('emailForSignIn');

      // Check if user exists in Firestore, if not create their document
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (!userDoc.exists()) {
        const displayName = result.user.email.split('@')[0];
        await setDoc(doc(db, 'users', result.user.uid), {
          uid: result.user.uid,
          email: result.user.email,
          displayName: displayName,
          createdAt: new Date().toISOString(),
          subscription: {
            plan: 'free',
            status: 'active'
          }
        });

        // Queue welcome email for new users
        try {
          await queueWelcomeEmail(result.user.email, displayName);
        } catch (emailError) {
          console.error('Failed to queue welcome email:', emailError);
          // Don't fail signin if email queueing fails
        }
      }

      return result.user;
    } catch (error) {
      console.error('Error completing magic link sign in:', error);
      throw error;
    }
  }
  return null;
};

// Sign Out
export const logOut = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Auth State Observer
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};
